<template>
  <div class="f-form">

    <div class="f-form__pair">
       <f-search-select
        :id="`event_rounds_${index}`"
        label="Rounds"
        :value="roundsValue"
        :options="roundsOptions"
        :searchable="false"
        :disabled="isCombined || !hasRounds"
        required
        :error="e('rounds')"
        @input="update('rounds', $event)"
        hint="Rounds including final"
      />
      <f-text
        :id="`event_max_submissions_${index}`"
        label="Maximum Submissions Per Person"
        type="number"
        min="0"
        :disabled="requiresHorse"
        hint="Leave blank or 0 if unlimited"
        :value="value.max_submissions"
        :error="e('max_submissions')"
        @input="update('max_submissions', $event)"
      />
    </div>

    <div class="f-form__pair">
      <f-text
        :id="`event_final_noms_${index}`"
        label="Final Noms"
        type="number"
        :value="value.final_noms"
        :error="e('final_noms')"
        @input="update('final_noms', $event)"
      />
      <f-text
        :id="`event_max_horses_${index}`"
        label="Max Horses"
        type="number"
        hint="Leave blank or 0 if unlimited"
        :disabled="!requiresHorse"
        :value="value.max_horses"
        :error="e('max_horses')"
        @input="update('max_horses', $event)"
      />
    </div>

    <div class="f-form__pair f-form__pair--3-1">
      <f-search-select
        :id="`event_type_${index}`"
        label="Type"
        :value="value.event_type_id"
        :options="eventTypes"
        track-by="id"
        option-label="name"
        placeholder="Select type..."
        :loading="loadingTypes"
        required
        :error="e('event_type_id')"
        @input="update('event_type_id', $event)"
      />
    </div>

    <div class="f-form__pair">
      <f-text
        :id="`riders_min_age_${index}`"
        label="Riders Min Age"
        type="number"
        :value="value.riders_min_age"
        :error="e('riders_min_age')"
        @input="update('riders_min_age', $event)"
      />
      <f-text
        :id="`riders_max_age_${index}`"
        label="Riders Max Age"
        type="number"
        :value="value.riders_max_age"
        hint="Leave blank or 0 if unlimited"
        :error="e('riders_max_age')"
        @input="update('riders_max_age', $event)"
      />
    </div>

    <div class="f-form__pair">
      <f-search-select
        :id="`riders_sex_${index}`"
        label="Rider Sex"
        :value="value.riders_sex"
        :options="riderSexOptions"
        :searchable="false"
        :error="e('riders_sex')"
        @input="update('riders_sex', $event)"
      />
      <div class="d-flex align-items-center h-100 pt-3">
        <f-checkbox
          :fontNormal="true"
          :labelBold="false"
          :id="`riders_maiden_${index}`"
          label="Maiden rider"
          :value="value.riders_maiden"
          @input="update('riders_maiden', $event)"
        />
      </div>
    </div>

    <div class="f-form__pair">
      <f-text
        :id="`back_amount_${index}`"
        label="Added Entry/Add Back"
        type="number"
        :value="value.back_amount"
        :error="e('back_amount')"
        @input="update('back_amount', $event)"
      />
      <div class="d-flex align-items-center h-100 pt-3">
        <f-checkbox
          :fontNormal="true"
          :labelBold="false"
          :id="`is_affiliated_${index}`"
          label="Yes, it is an affiliated event"
          :value="value.is_affiliated"
          @input="update('is_affiliated', $event)"
        />
      </div>
    </div>
    <div class="f-form__half">
      <f-textarea
        :id="`notes_${index}`"
        label="Notes"
        :rows="6"
        :value="value.notes"
        :error="e('notes')"
        @input="update('notes', $event)"
      />
    </div>
    <div class="f-form__pair">
      <f-text
        :id="`horses_min_age_${index}`"
        label="Horse Min Age"
        type="number"
        hint="Leave blank or 0 if unlimited"
        :value="value.horses_min_age"
        :disabled="!hasHorseRestrictions"
        :error="e('horses_min_age')"
        @input="update('horses_min_age', $event)"
      />
      <div class="d-flex flex-column" style="gap: 1rem">
        <f-text
          :id="`horses_max_age_${index}`"
          label="Horse Max Age"
          type="number"
          hint="Leave blank or 0 if unlimited"
          :value="value.horses_max_age"
          :disabled="isFuturity || !hasHorseRestrictions"
          :error="e('horses_max_age')"
          @input="update('horses_max_age', $event)"
        />
        <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`hc1_${index}`" label="Futurity" :value="value.horses_class_1" @input="update('horses_class_1', $event)" />
        <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`hc2_${index}`" label="Maiden"   :value="value.horses_class_2" @input="update('horses_class_2', $event)" />
        <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`hc3_${index}`" label="Novice"   :value="value.horses_class_3" @input="update('horses_class_3', $event)" />
        <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`hc4_${index}`" label="Open"     :value="value.horses_class_4" @input="update('horses_class_4', $event)" />
        <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`hc5_${index}`" label="Any"      :value="value.horses_class_5" @input="update('horses_class_5', $event)" />
        <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`ashs_${index}`" label="ASHS" :value="value.ashs" @input="update('ashs', $event)" />
        <div>
          <f-checkbox :fontNormal="true" :disabled="!hasHorseRestrictions" :id="`aqha_${index}`" label="AQHA" :value="value.aqha" @input="update('aqha', $event)" />
          <small v-if="e('aqha')" class="text-danger d-block">{{ e('aqha') }}</small>
        </div>
      </div>
     </div>
  </div>
</template>

<script>
import FText from '@/components/FormFields/FText'
import FTextarea from '@/components/FormFields/FTextarea'
import FCheckbox from '@/components/FormFields/FCheckbox'
import FSearchSelect from '@/components/FormFields/FSearchSelect'

const ROUNDS_OPTIONS = [
  { id: 1, name: '1' },
  { id: 2, name: '2' },
  { id: 3, name: '3' },
  { id: 4, name: '4' },
  { id: 5, name: '5' }
]

export default {
  name: 'EventFieldsSection',
  components: { FText, FTextarea, FCheckbox, FSearchSelect },
  props: {
    value: { type: Object, required: true },
    eventTypes: { type: Array, default: () => [] },
    loadingTypes: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) },
    index: { type: Number, default: 0 },
    isCombined: { type: Boolean, default: false }
  },
  data() {
    return {
      roundsOptions: ROUNDS_OPTIONS,
      riderSexOptions: [
        { id: 1, name: 'Male' },
        { id: 2, name: 'Female' },
        { id: 3, name: 'All' }
      ]
    }
  },
  computed: {
    roundsValue() {
      if (this.isCombined) return 2
      return this.value.rounds
    },
    isFuturity() {
      if (!this.value.event_type_id) return false
      const selected = this.eventTypes.find(t => t.id === this.value.event_type_id)
      return selected != null && selected.name.toLowerCase() === 'futurity'
    },
    selectedTypeDefaults() {
      const selected = this.eventTypes.find(t => t.id === this.value.event_type_id)
      return selected != null ? selected.defaults : {}
    },
    requiresHorse() {
      return this.selectedTypeDefaults.requires_horse !== false
    },
    hasHorseRestrictions() {
      return this.selectedTypeDefaults.has_horse_restrictions !== false
    },
    hasRounds() {
      return this.selectedTypeDefaults.has_rounds !== false
    }
  },
  watch: {
    isFuturity(val) {
      if (val) this.$emit('input', { ...this.value, horses_max_age: 5 })
    }
  },
  methods: {
    e(field) {
      const key = `events.${this.index}.${field}`
      return this.errors[key] ? this.errors[key][0] : null
    },
    update(field, val) {
      if (field === 'ashs' && val) {
        this.$emit('input', { ...this.value, ashs: true, aqha: false })
      } else if (field === 'aqha' && val) {
        this.$emit('input', { ...this.value, ashs: false, aqha: true })
      } else {
        this.$emit('input', { ...this.value, [field]: val })
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Events/EventFieldsSection.vue